import React, { useState, useRef, useEffect } from "react";
import { useFormik, FormikProps } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import { getAuthCookie } from "../../utils/authCookies";

type State = {
    _id: string;
    countryId: string;
    name: string;
    code: string;
};

type Country = {
    _id: string;
    name: string;
    code: string;
};

const SignageEditState = () => {
  const { stateId } = useParams<{ stateId: string }>();
  const [state, setState] = useState<State | null>(null);
  const [countries, setCountries] = useState<Country[]>([]);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const navigate = useNavigate();
  const token = getAuthCookie("sessionToken");
  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
        const fetchState = async () => {
          try {
            setLoading(true);
            const response = await fetch(
              `${process.env.REACT_APP_API_URL}/location/states/${stateId}`,
              {
                method: "GET",
                headers: {
                  Authorization: `Bearer ${token}`,
                  "Content-Type": "application/json",
                },
              }
            );
            if (!response.ok) {
              throw new Error("Failed to fetch state data");
            }
            const data = await response.json();
            setState(data);
          } catch (err) {
            console.error("Error fetching state data:", err);
            toast.error("Error fetching state data. Please try again later.");
          } finally {
            setLoading(false);
          }
        };
    
        if (stateId) fetchState();
      }, [stateId, token]);

  useEffect(() => {
      const fetchCountries = async () => {
        try {
          const response = await fetch(
            `${process.env.REACT_APP_API_URL}/location/countries`,
            {
              method: "GET",
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
            }
          );
          const data = await response.json();
          setCountries(data);
        } catch (err) {
          setError(
            err instanceof Error ? err.message : "An unknown error occurred"
          );
        } finally {
          setLoading(false);
        }
      };
  
      fetchCountries();
    }, [token]);

  const formik = useFormik<{
    countryId: string;
    name: string;
    code: string;
  }>({
    enableReinitialize: true,
    initialValues: {
      countryId: state?.countryId || '',
      name: state?.name || '',
      code: state?.code || '',
    },
    validationSchema: Yup.object({
      name: Yup.string()
        .min(2, "Name must be at least 2 characters long")
        .max(50, "Name cannot be longer than 50 characters")
        .required("Name is required"),
      code: Yup.string().required("Code is required"),
    }),
    onSubmit: async (values) => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/location/states/${stateId}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            countryId: values.countryId,
            name: values.name,
            code: values.code,
          }),
        });
    
        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.message || "Failed to create state");
        }
    
        navigate("/signage/states", {
          state: { successMessage: "State added successfully!" },
        });
        toast.success("State added successfully!");
      } catch (error: any) {
        console.error(error);
        toast.error(`Error: ${error.message}`);
      } finally {
        setFormSubmitting(false);
      }
    }        
  });

  return (
    <div className="content">
      <div className="card">
        <div className="card-body">
          <h2>Edit State</h2>

          <div className="row justify-content-center">
            <div className="col-md-12 col-lg-12 col-xl-12">
              <div className="card mt-4 card-bg-fill">
                <div className="card-body p-4">
                  <div className="p-2 mt-4">
                    <form onSubmit={formik.handleSubmit}>
                      <div className="row">
                        <div className="col-md-12">
                          <div className="row">
                            {/* Name Field */}
                            <div className="col-md-4 mb-3">
                              <label className="form-label">
                                Country <span className="text-danger">*</span>
                              </label>
                              <select
                                className="form-control"
                                id="countryId"
                                name="countryId"
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.countryId}
                              >
                                <option value="">-Select-</option>
                                {countries!=null && countries?.map((country,index)=>(
                                    <option value={country._id}>{country.name}</option>
                                ))}
                                </select>
                              {formik.touched.countryId && formik.errors.countryId ? (
                                <div className="invalid-feedback">
                                  {formik.errors.countryId}
                                </div>
                              ) : null}
                            </div>
                            <div className="col-md-4 mb-3">
                              <label className="form-label">
                                State Name <span className="text-danger">*</span>
                              </label>
                              <input
                                type="text"
                                className="form-control"
                                id="name"
                                name="name"
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.name}
                              />
                              {formik.touched.name && formik.errors.name ? (
                                <div className="invalid-feedback">
                                  {formik.errors.name}
                                </div>
                              ) : null}
                            </div>
                            <div className="col-md-4 mb-3">
                              <label className="form-label">
                                State Code <span className="text-danger">*</span>
                              </label>
                              <input
                                type="text"
                                className="form-control"
                                id="code"
                                name="code"
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.code}
                              />
                              {formik.touched.code && formik.errors.code ? (
                                <div className="invalid-feedback">
                                  {formik.errors.code}
                                </div>
                              ) : null}
                            </div>                            
                          </div>
                        </div>
                       
                      </div>

                      {/* Submit Button */}
                      <div className="mt-4">
                        <button
                          className="btn btn-success w-100"
                          type="submit"
                          disabled={formSubmitting}
                        >
                          {formSubmitting ? "Submitting..." : "Submit"}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignageEditState;
